import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, DatePicker, Tag, Space,
    InputNumber, message, Popconfirm, Typography, Tooltip, Upload,
    AutoComplete, Divider, Card, Row, Col, Grid, List, Avatar, theme,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    UploadOutlined, FileExcelOutlined, MinusCircleOutlined,
    CalendarOutlined, ClearOutlined, InboxOutlined,
    MoreOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeApi, filesApi } from '../../../api/client';
import { useSearchParams } from 'react-router-dom';
import CostDetailDrawer from './CostDetailDrawer';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Polish VAT rates
const VAT_RATES = [
    { value: 23, label: '23%' },
    { value: 8, label: '8%' },
    { value: 5, label: '5%' },
    { value: 0, label: '0%' },
    { value: -1, label: 'zw.' },
    { value: -2, label: 'np.' },
];

const COST_CATEGORIES = [
    { value: 'PROCUREMENT', label: 'Zaopatrzenie' },
    { value: 'COGS', label: 'Koszt sprzedanych towarów' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'OPERATIONS', label: 'Operacje' },
    { value: 'PERSONNEL', label: 'Kadry' },
    { value: 'TECHNOLOGY', label: 'Technologia' },
    { value: 'LOGISTICS', label: 'Logistyka' },
    { value: 'LEGAL', label: 'Prawne' },
    { value: 'OTHER', label: 'Inne' },
];

const PAYMENT_STATUSES = [
    { value: 'UNPAID', label: 'Nieopłacone', color: 'red' },
    { value: 'PARTIALLY_PAID', label: 'Częściowo', color: 'orange' },
    { value: 'PAID', label: 'Opłacone', color: 'green' },
    { value: 'OVERDUE', label: 'Przeterminowane', color: 'volcano' },
];

interface CostsActualTabProps {
    dateRange?: [string, string];
    initialOverdueFilter?: boolean;
}

const CostsActualTab: React.FC<CostsActualTabProps> = ({ dateRange, initialOverdueFilter }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCost, setEditingCost] = useState<any>(null);
    const [drawerCostId, setDrawerCostId] = useState<string | null>(null);
    const [filters, setFilters] = useState<any>(initialOverdueFilter ? { overdue: true } : {});
    const [filterDateRange, setFilterDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
    const [vendorOptions, setVendorOptions] = useState<any[]>([]);
    const [purposeOptions, setPurposeOptions] = useState<any[]>([]);
    const queryClient = useQueryClient();
    const { token } = theme.useToken();

    // Sync external overdue filter prop
    useEffect(() => {
        if (initialOverdueFilter) {
            setFilters((p: any) => ({ ...p, overdue: true }));
        } else {
            setFilters((p: any) => {
                const { overdue, ...rest } = p;
                return rest;
            });
        }
    }, [initialOverdueFilter]);

    // Load purpose options on mount
    useEffect(() => {
        financeApi.getCostPurposes().then(res => {
            setPurposeOptions((res.data || []).map((p: string) => ({ value: p, label: p })));
        }).catch(() => { });
    }, []);

    // Deep link support
    useEffect(() => {
        const costId = searchParams.get('costId');
        if (costId) {
            setDrawerCostId(costId);
        }
    }, [searchParams]);

    // Fetch costs
    const { data: costs = [], isLoading } = useQuery({
        queryKey: ['costs', dateRange, filters],
        queryFn: async () => {
            const params: any = { ...filters };
            if (dateRange) {
                params.startDate = dateRange[0];
                params.endDate = dateRange[1];
            }
            const res = await financeApi.getCosts(params);
            return res.data;
        },
    });

    // Fetch initiatives
    const { data: initiatives = [] } = useQuery({
        queryKey: ['strategic-initiatives'],
        queryFn: async () => (await financeApi.getStrategicInitiatives()).data,
    });

    // Fetch budget grid for position assignment
    const { data: budgetGrid = [] } = useQuery({
        queryKey: ['budget-grid-for-assign', new Date().getFullYear()],
        queryFn: async () => (await financeApi.getBudgetGrid(new Date().getFullYear())).data,
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: (data: any) => financeApi.createCost(data),
        onSuccess: async (response) => {
            // Save line items if any
            if (lineItems.length > 0) {
                await financeApi.saveCostLineItems(response.data.id, lineItems);
            }
            // Upload files
            for (const f of uploadedFiles) {
                if (f.originFileObj) {
                    await filesApi.upload(f.originFileObj, 'COST', response.data.id);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['costs'] });
            queryClient.invalidateQueries({ queryKey: ['costs-overview'] });
            message.success('Koszt dodany');
            closeModal();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => financeApi.updateCost(id, data),
        onSuccess: async () => {
            if (editingCost?.id && lineItems.length > 0) {
                await financeApi.saveCostLineItems(editingCost.id, lineItems);
            }
            // Upload new files
            for (const f of uploadedFiles) {
                if (f.originFileObj && !f.existingId) {
                    await filesApi.upload(f.originFileObj, 'COST', editingCost.id);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['costs'] });
            queryClient.invalidateQueries({ queryKey: ['costs-overview'] });
            message.success('Koszt zaktualizowany');
            closeModal();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => financeApi.deleteCost(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['costs'] });
            queryClient.invalidateQueries({ queryKey: ['costs-overview'] });
            message.success('Koszt usunięty');
        },
    });

    // Vendor autocomplete
    const handleVendorSearch = useCallback(async (value: string) => {
        if (value.length < 1) return;
        try {
            const res = await financeApi.getVendors(value);
            setVendorOptions((res.data || []).map((v: any) => ({ value: v.name, label: v.name })));
        } catch { setVendorOptions([]); }
    }, []);

    // Line item helpers
    const calcLineItem = (item: any, sourceField?: 'net' | 'gross') => {
        const qty = Number(item.quantity || 1);
        const vatRate = Number(item.vatRate ?? 23);
        const effectiveVat = vatRate < 0 ? 0 : vatRate;
        const discountType = item.discountType;
        const discountValue = Number(item.discountValue || 0);

        let unitNet = Number(item.unitPriceNet || 0);
        let unitGross = Number(item.unitPriceGross || 0);

        if (sourceField === 'gross') {
            unitNet = unitGross / (1 + effectiveVat / 100);
        } else {
            unitGross = unitNet * (1 + effectiveVat / 100);
        }

        const baseNet = qty * unitNet;
        let discountAmount = 0;
        if (discountType === 'AMOUNT') discountAmount = discountValue;
        else if (discountType === 'PERCENT') discountAmount = baseNet * (discountValue / 100);

        const net = Math.max(0, baseNet - discountAmount);
        const vat = net * (effectiveVat / 100);
        const gross = net + vat;

        return {
            ...item,
            unitPriceNet: Math.round(unitNet * 100) / 100,
            unitPriceGross: Math.round(unitGross * 100) / 100,
            netAmount: Math.round(net * 100) / 100,
            vatAmount: Math.round(vat * 100) / 100,
            grossAmount: Math.round(gross * 100) / 100
        };
    };

    const addLineItem = () => {
        setLineItems(prev => [...prev, calcLineItem({
            name: '', quantity: 1, unitPriceNet: 0, unitPriceGross: 0, vatRate: 23,
            discountType: null, discountValue: 0, netAmount: 0, vatAmount: 0, grossAmount: 0
        })]);
    };

    const updateLineItem = (index: number, field: string, value: any) => {
        setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            const sourceField = field === 'unitPriceGross' ? 'gross' : 'net';
            updated[index] = calcLineItem(updated[index], sourceField as any);
            return updated;
        });
    };

    const removeLineItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const totalNet = useMemo(() => lineItems.reduce((s, i) => s + Number(i.netAmount || 0), 0), [lineItems]);
    const totalGross = useMemo(() => lineItems.reduce((s, i) => s + Number(i.grossAmount || 0), 0), [lineItems]);

    // Modal
    const openAddModal = () => {
        form.resetFields();
        setEditingCost(null);
        setLineItems([]);
        setUploadedFiles([]);
        setModalVisible(true);
    };

    const openEditModal = async (cost: any) => {
        setEditingCost(cost);
        form.setFieldsValue({
            ...cost,
            date: cost.date ? dayjs(cost.date) : null,
            dueDate: cost.dueDate ? dayjs(cost.dueDate) : null,
            recurrenceEndDate: cost.recurrenceEndDate ? dayjs(cost.recurrenceEndDate) : null,
        });
        // Load line items
        try {
            const res = await financeApi.getCostLineItems(cost.id);
            setLineItems((res.data || []).map((li: any) => ({
                ...li,
                quantity: Number(li.quantity || 1),
                unitPriceNet: Number(li.unitPriceNet || 0),
                unitPriceGross: Number(li.unitPriceGross || 0),
                vatRate: Number(li.vatRate ?? 23),
                discountValue: Number(li.discountValue || 0),
                netAmount: Number(li.netAmount || 0),
                vatAmount: Number(li.vatAmount || 0),
                grossAmount: Number(li.grossAmount || 0),
            })));
        } catch { setLineItems([]); }
        // Load files
        try {
            const authStore = localStorage.getItem('curecraft-auth');
            let token = '';
            if (authStore) {
                try { token = JSON.parse(authStore).state?.token || ''; } catch { }
            }
            const res = await filesApi.getByEntity('COST', cost.id);
            setUploadedFiles((res.data || []).map((f: any) => ({
                uid: f.id,
                name: f.originalName,
                status: 'done',
                existingId: f.id,
                url: `${filesApi.getDownloadUrl(f.id)}?token=${token}`,
            })));
        } catch { setUploadedFiles([]); }
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingCost(null);
        setLineItems([]);
        setUploadedFiles([]);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const data = {
                ...values,
                date: values.date?.format('YYYY-MM-DD'),
                dueDate: values.dueDate?.format('YYYY-MM-DD'),
                recurrenceEndDate: values.recurrenceEndDate?.format('YYYY-MM-DD'),
                amount: lineItems.length > 0 ? totalGross : Number(values.amount || 0),
                totalNet: lineItems.length > 0 ? totalNet : undefined,
                totalGross: lineItems.length > 0 ? totalGross : undefined,
            };

            if (editingCost) {
                updateMutation.mutate({ id: editingCost.id, data });
            } else {
                createMutation.mutate(data);
            }
        } catch { /* validation */ }
    };

    // Vendor autocomplete
    const handlePurposeSearch = useCallback(async (value: string) => {
        if (value.length < 1) return;
        try {
            const res = await financeApi.getCostPurposes(value);
            setPurposeOptions((res.data || []).map((p: string) => ({ value: p, label: p })));
        } catch { setPurposeOptions([]); }
    }, []);

    // Export
    const handleExport = () => {
        const params = new URLSearchParams();
        if (dateRange) {
            params.set('startDate', dateRange[0]);
            params.set('endDate', dateRange[1]);
        }
        const authStore = localStorage.getItem('curecraft-auth');
        let token = '';
        if (authStore) {
            try { token = JSON.parse(authStore).state?.token || ''; } catch { }
        }
        const url = `${financeApi.exportCostsUrl}?${params.toString()}`;
        window.open(`${url}&token=${token}`, '_blank');
    };

    // Table columns
    const columns = [
        {
            title: 'Data', dataIndex: 'date', key: 'date', width: 100,
            render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY') : '-',
            sorter: (a: any, b: any) => {
                const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
                if (dateCompare !== 0) return dateCompare;
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            },
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Nazwa', dataIndex: 'name', key: 'name', width: 200,
            render: (name: string, record: any) => (
                <a onClick={() => setDrawerCostId(record.id)}>{name}</a>
            ),
        },
        {
            title: 'Dostawca', dataIndex: 'vendorName', key: 'vendorName', width: 150,
            render: (v: string) => v || <Text type="secondary">—</Text>,
        },
        {
            title: 'Cel', dataIndex: 'costPurposes', key: 'costPurposes', width: 160,
            render: (v: string[]) => v && v.length > 0 ? v.map((t: string) => <Tag key={t} color="purple">#{t}</Tag>) : <Text type="secondary">—</Text>,
        },
        {
            title: 'Kategoria', dataIndex: 'category', key: 'category', width: 140,
            render: (c: string) => {
                const cat = COST_CATEGORIES.find(cc => cc.value === c);
                return <Tag>{cat?.label || c}</Tag>;
            },
            filters: COST_CATEGORIES.map(c => ({ text: c.label, value: c.value })),
            onFilter: (value: any, record: any) => record.category === value,
        },
        {
            title: 'Kwota netto', dataIndex: 'totalNet', key: 'totalNet', width: 120, align: 'right' as const,
            render: (v: number) => v != null ? `${Number(v).toFixed(2)} PLN` : <Text type="secondary">—</Text>,
        },
        {
            title: 'Kwota brutto', dataIndex: 'amount', key: 'amount', width: 130, align: 'right' as const,
            render: (v: number) => <Text strong>{Number(v || 0).toFixed(2)} PLN</Text>,
            sorter: (a: any, b: any) => Number(a.amount) - Number(b.amount),
        },
        {
            title: 'Status', dataIndex: 'paymentStatus', key: 'paymentStatus', width: 120,
            render: (s: string, record: any) => {
                const st = PAYMENT_STATUSES.find(ps => ps.value === s);
                if (s === 'PARTIALLY_PAID' && record.paidAmount != null) {
                    return (
                        <Tooltip title={`Zapłacono: ${Number(record.paidAmount || 0).toFixed(2)} PLN`}>
                            <Tag color={st?.color || 'default'}>{st?.label || s}</Tag>
                        </Tooltip>
                    );
                }
                return <Tag color={st?.color || 'default'}>{st?.label || s}</Tag>;
            },
        },
        {
            title: 'Termin', dataIndex: 'dueDate', key: 'dueDate', width: 100,
            render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY') : '-',
        },
        {
            title: 'Akcje', key: 'actions', width: 90, fixed: 'right' as const,
            render: (_: any, record: any) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edytuj"><Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEditModal(record); }} /></Tooltip>
                    <Popconfirm title="Usunąć koszt?" onConfirm={() => deleteMutation.mutate(record.id)} okText="Tak" cancelText="Nie">
                        <Tooltip title="Usuń"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Filter UI
    const filterBar = (
        <Space wrap style={{ marginBottom: 16, width: isMobile ? '100%' : 'auto' }}>
            <Input.Search
                placeholder="Szukaj..."
                allowClear
                style={{ width: isMobile ? '100%' : 200 }}
                onSearch={(v) => setFilters((p: any) => ({ ...p, search: v || undefined }))}
            />
            {!isMobile && (
                <>
                    <DatePicker.RangePicker
                        value={filterDateRange}
                        onChange={(dates) => {
                            setFilterDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
                            if (dates && dates[0] && dates[1]) {
                                setFilters((p: any) => ({
                                    ...p,
                                    startDate: dates[0]!.format('YYYY-MM-DD'),
                                    endDate: dates[1]!.format('YYYY-MM-DD'),
                                }));
                            } else {
                                setFilters((p: any) => {
                                    const { startDate, endDate, ...rest } = p;
                                    return rest;
                                });
                            }
                        }}
                        format="DD.MM.YYYY"
                        placeholder={['Od', 'Do']}
                        style={{ width: 240 }}
                        suffixIcon={<CalendarOutlined />}
                    />
                    <Select placeholder="Kategoria" allowClear style={{ width: 160 }} onChange={(v) => setFilters((p: any) => ({ ...p, category: v || undefined }))}>
                        {COST_CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                    <Select placeholder="Status" allowClear style={{ width: 140 }} value={filters.paymentStatus} onChange={(v) => setFilters((p: any) => ({ ...p, paymentStatus: v || undefined }))}>
                        {PAYMENT_STATUSES.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                    </Select>
                </>
            )}
            {isMobile && (
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                    <Select placeholder="Status" allowClear style={{ flex: 1 }} value={filters.paymentStatus} onChange={(v) => setFilters((p: any) => ({ ...p, paymentStatus: v || undefined }))}>
                        {PAYMENT_STATUSES.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                    </Select>
                    <Button
                        icon={<ClearOutlined />}
                        onClick={() => {
                            setFilters({});
                            setFilterDateRange(null);
                        }}
                        disabled={!Object.keys(filters).length}
                    />
                </div>
            )}
            {!isMobile && Object.keys(filters).some(k => filters[k] !== undefined) && (
                <Button
                    icon={<ClearOutlined />}
                    onClick={() => {
                        setFilters({});
                        setFilterDateRange(null);
                    }}
                    size="small"
                    type="dashed"
                >
                    Wyczyść
                </Button>
            )}
        </Space>
    );

    const costType = Form.useWatch('costType', form);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
                <div>{filterBar}</div>
                <Space style={{ width: isMobile ? '100%' : 'auto' }}>
                    {!isMobile && <Button icon={<FileExcelOutlined />} onClick={handleExport}>Eksport Excel</Button>}
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal} block={isMobile}>Dodaj koszt</Button>
                </Space>
            </div>

            {isMobile ? (
                <List
                    dataSource={costs}
                    loading={isLoading}
                    pagination={{ pageSize: 10, size: 'small', simple: true, style: { textAlign: 'center', marginTop: 16 } }}
                    renderItem={(record: any) => {
                        const st = PAYMENT_STATUSES.find(ps => ps.value === record.paymentStatus);
                        return (
                            <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                <Card
                                    size="small"
                                    onClick={() => setDrawerCostId(record.id)}
                                    style={{ width: '100%', cursor: 'pointer', borderRadius: 8 }}
                                    actions={[
                                        <Button type="text" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEditModal(record); }}>Edytuj</Button>,
                                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(record.id); }}>Usuń</Button>
                                    ]}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{record.name}</div>
                                            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                                {record.date ? dayjs(record.date).format('DD.MM.YYYY') : '-'} • {record.vendorName || '-'}
                                            </div>
                                        </div>
                                        <Tag color={st?.color || 'default'} style={{ margin: 0 }}>{st?.label || record.paymentStatus}</Tag>
                                    </div>
                                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                        <div>
                                            {record.costPurposes?.length > 0 && (
                                                <div style={{ marginBottom: 4 }}>
                                                    {record.costPurposes.map((t: string) => <Tag key={t} size="small" color="purple" style={{ fontSize: 10 }}>#{t}</Tag>)}
                                                </div>
                                            )}
                                            <Text type="secondary" style={{ fontSize: 12 }}>Brutto:</Text>
                                        </div>
                                        <Text strong style={{ fontSize: 16, color: token.colorPrimary }}>{Number(record.amount || 0).toFixed(2)} PLN</Text>
                                    </div>
                                </Card>
                            </List.Item>
                        );
                    }}
                />
            ) : (
                <Table
                    dataSource={costs}
                    columns={columns}
                    rowKey="id"
                    loading={isLoading}
                    size="small"
                    scroll={{ x: 1400 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    onRow={(record) => ({
                        onClick: () => setDrawerCostId(record.id),
                        style: { cursor: 'pointer' },
                    })}
                />
            )}


            {/* Add/Edit Modal */}
            <Modal
                title={editingCost ? 'Edytuj koszt' : 'Dodaj nowy koszt'}
                open={modalVisible}
                onCancel={closeModal}
                onOk={handleSubmit}
                width={900}
                okText={editingCost ? 'Zapisz' : 'Dodaj'}
                cancelText="Anuluj"
                confirmLoading={createMutation.isPending || updateMutation.isPending}
            >
                <Form form={form} layout="vertical" initialValues={{ costType: 'ONE_TIME', paymentStatus: 'UNPAID', category: 'OTHER' }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="name" label="Nazwa kosztu" rules={[{ required: true, message: 'Podaj nazwę' }]}>
                                <Input placeholder="np. Faktura za hosting" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="vendorName" label="Dostawca">
                                <AutoComplete
                                    placeholder="Wpisz lub wybierz dostawcę"
                                    options={vendorOptions}
                                    onSearch={handleVendorSearch}
                                    filterOption={false}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item name="date" label="Data" rules={[{ required: true, message: 'Podaj datę' }]}>
                                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="category" label="Kategoria">
                                <Select>{COST_CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}</Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="paymentStatus" label="Status płatności">
                                <Select>{PAYMENT_STATUSES.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}</Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="dueDate" label="Termin płatności">
                                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="invoiceNumber" label="Nr faktury">
                                <Input placeholder="FV/2026/02/001" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="costType" label="Typ">
                                <Select>
                                    <Option value="ONE_TIME">Jednorazowy</Option>
                                    <Option value="RECURRING">Cykliczny</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="initiativeId" label="Inicjatywa">
                                <Select allowClear placeholder="Opcjonalnie">
                                    {initiatives.map((i: any) => <Option key={i.id} value={i.id}>{i.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {costType === 'RECURRING' && (
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="recurrenceFrequency" label="Częstotliwość">
                                    <Select>
                                        <Option value="WEEKLY">Tygodniowo</Option>
                                        <Option value="MONTHLY">Miesięcznie</Option>
                                        <Option value="QUARTERLY">Kwartalnie</Option>
                                        <Option value="YEARLY">Rocznie</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="recurrenceDay" label="Dzień">
                                    <InputNumber min={1} max={31} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="recurrenceEndDate" label="Koniec">
                                    <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Budget assignment */}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="budgetPositionId" label="Pozycja budżetowa">
                                <Select allowClear placeholder="Przypisz do pozycji budżetowej">
                                    {budgetGrid.map((row: any) => (
                                        <Option key={row.id} value={row.id}>{row.name} ({row.category})</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="description" label="Opis">
                                <Input.TextArea rows={1} placeholder="Dodatkowy opis..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="costPurposes" label="Cel kosztów (hashtagi)">
                                <Select
                                    mode="tags"
                                    placeholder="Wpisz cele, np. targi, ezofest"
                                    options={purposeOptions}
                                    tokenSeparators={[',']}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) => prev.paymentStatus !== curr.paymentStatus}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('paymentStatus') === 'PARTIALLY_PAID' ? (
                                        <Form.Item name="paidAmount" label="Kwota wpłacona (PLN)">
                                            <InputNumber
                                                min={0}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                placeholder="0.00"
                                                decimalSeparator=","
                                            />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Invoice file upload */}
                    <Form.Item label="Faktura / załączniki">
                        <Upload.Dragger
                            fileList={uploadedFiles}
                            onChange={({ fileList }) => setUploadedFiles(fileList)}
                            beforeUpload={() => false}
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                        >
                            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                            <p className="ant-upload-text">Przeciągnij plik lub kliknij</p>
                            <p className="ant-upload-hint">PDF, JPG, PNG, Excel, Word</p>
                        </Upload.Dragger>
                    </Form.Item>

                    {/* Line Items */}
                    <Divider orientation="left" orientationMargin={0}>
                        Pozycje kosztowe (z faktury)
                    </Divider>

                    <Row gutter={8} style={{ marginBottom: 4, fontWeight: 'bold', fontSize: 12 }}>
                        <Col span={6}>Opis pozycji</Col>
                        <Col span={2}>Ilość</Col>
                        <Col span={3}>Cena netto</Col>
                        <Col span={3}>VAT</Col>
                        <Col span={3}>Cena brutto</Col>
                        <Col span={3}>Rabat</Col>
                        <Col span={4}></Col>
                    </Row>


                    {lineItems.map((item, idx) => (
                        <Card key={idx} size="small" style={{ marginBottom: 8, background: 'var(--ant-color-bg-container-hover, #fafafa)' }} extra={
                            <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeLineItem(idx)} />
                        }>
                            <Row gutter={8} align="middle">
                                <Col span={6}>
                                    <Input value={item.name} onChange={e => updateLineItem(idx, 'name', e.target.value)} size="small" placeholder="Opis" />
                                </Col>
                                <Col span={2}>
                                    <InputNumber value={item.quantity} onChange={v => updateLineItem(idx, 'quantity', v)} min={0.01} step={1} size="small" style={{ width: '100%' }} />
                                </Col>
                                <Col span={3}>
                                    <InputNumber value={item.unitPriceNet} onChange={v => updateLineItem(idx, 'unitPriceNet', v)} min={0} precision={2} size="small" style={{ width: '100%' }} decimalSeparator="," />
                                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>Razem Netto: {Number(item.netAmount || 0).toFixed(2)} zł</div>
                                </Col>
                                <Col span={3}>
                                    <Select value={item.vatRate} onChange={v => updateLineItem(idx, 'vatRate', v)} size="small" style={{ width: '100%' }}>
                                        {VAT_RATES.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
                                    </Select>
                                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>Kwota VAT: {Number(item.vatAmount || 0).toFixed(2)} zł</div>
                                </Col>
                                <Col span={3}>
                                    <InputNumber value={item.unitPriceGross} onChange={v => updateLineItem(idx, 'unitPriceGross', v)} min={0} precision={2} size="small" style={{ width: '100%' }} decimalSeparator="," />
                                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>Razem Brutto: {Number(item.grossAmount || 0).toFixed(2)} zł</div>
                                </Col>
                                <Col span={3}>
                                    <Select value={item.discountType || undefined} onChange={v => updateLineItem(idx, 'discountType', v || null)} size="small" style={{ width: '100%' }} allowClear placeholder="Rabat">
                                        <Option value="AMOUNT">Kwota</Option>
                                        <Option value="PERCENT">%</Option>
                                    </Select>
                                </Col>
                                <Col span={4}>
                                    {item.discountType && (
                                        <InputNumber
                                            value={item.discountValue}
                                            onChange={v => updateLineItem(idx, 'discountValue', v)}
                                            min={0}
                                            precision={2}
                                            size="small"
                                            style={{ width: '100%' }}
                                            addonAfter={item.discountType === 'PERCENT' ? '%' : 'zł'}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    ))}

                    <Button type="dashed" onClick={addLineItem} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                        Dodaj pozycję
                    </Button>

                    {lineItems.length > 0 && (
                        <Card size="small" style={{ marginTop: 12, border: `1px solid var(--ant-color-primary-border)`, background: 'var(--ant-color-primary-bg)' }}>
                            <Row gutter={16} justify="end">
                                <Col span={6} style={{ textAlign: 'right' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Łącznie Netto</Text>
                                    <div><Text strong style={{ fontSize: 16 }}>{totalNet.toFixed(2)} PLN</Text></div>
                                </Col>
                                <Col span={6} style={{ textAlign: 'right' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Łącznie VAT</Text>
                                    <div><Text strong style={{ fontSize: 16 }}>{(totalGross - totalNet).toFixed(2)} PLN</Text></div>
                                </Col>
                                <Col span={6} style={{ textAlign: 'right' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Łącznie Brutto</Text>
                                    <div><Text strong style={{ fontSize: 20, color: 'var(--ant-color-primary)' }}>{totalGross.toFixed(2)} PLN</Text></div>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Form.Item name="notes" label="Notatki" style={{ marginTop: 12 }}>
                        <Input.TextArea rows={2} placeholder="Dodatkowe notatki..." />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Detail Drawer */}
            <CostDetailDrawer
                costId={drawerCostId}
                onClose={() => {
                    setDrawerCostId(null);
                    searchParams.delete('costId');
                    setSearchParams(searchParams);
                }}
            />
        </div>
    );
};

export default CostsActualTab;
